import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { TrackLocations } from "@/hooks/use-segment";
import { useCreateWebhook } from "@/queries/webhook-queries";
import { WebhookProvider, WebhookType } from "gitpod-next-api/gitpod/v1/webhook_pb";
import { useCallback, useId, useMemo, type FC } from "react";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { WEBHOOK_PROVIDER_CONFIG, WEBHOOK_TYPE_CONFIG } from "@/components/webhooks/webhook-config";
import { atom, useAtom, useSetAtom, useStore, type PrimitiveAtom } from "jotai";

type CreateWebhookDialogProps = {
    open: boolean;
    onClose: () => void;
    onSuccess: (webhookId: string) => void;
};

type FormErrorMessages = {
    name?: string;
    type?: string;
    scope?: string;
    provider?: string;
};

type FormAtoms = {
    nameAtom: PrimitiveAtom<string>;
    descriptionAtom: PrimitiveAtom<string>;
    typeAtom: PrimitiveAtom<WebhookType | null>;
    scopeAtom: PrimitiveAtom<string>;
    providerAtom: PrimitiveAtom<WebhookProvider | null>;
    errorMessagesAtom: PrimitiveAtom<FormErrorMessages>;
};

export const CreateWebhookDialog: FC<CreateWebhookDialogProps> = ({ open, onClose, onSuccess }) => {
    const { toast } = useToast();
    const { mutateAsync: createWebhook, isPending } = useCreateWebhook();
    const store = useStore();
    const atoms = useFormAtoms();
    const formId = useId();

    const resetForm = useCallback(() => {
        store.set(atoms.nameAtom, "");
        store.set(atoms.descriptionAtom, "");
        store.set(atoms.typeAtom, null);
        store.set(atoms.scopeAtom, "");
        store.set(atoms.providerAtom, null);
        store.set(atoms.errorMessagesAtom, {});
    }, [store, atoms]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                resetForm();
                onClose();
            }
        },
        [onClose, resetForm],
    );

    const validateForm = useCallback((): boolean => {
        const name = store.get(atoms.nameAtom);
        const type = store.get(atoms.typeAtom);
        const scope = store.get(atoms.scopeAtom);
        const provider = store.get(atoms.providerAtom);

        const newErrorMessages: FormErrorMessages = {};

        if (!name.trim()) {
            newErrorMessages.name = "Please provide a name for this webhook";
        } else if (name.length > 80) {
            newErrorMessages.name = "Name must be 80 characters or less";
        }

        if (!type) {
            newErrorMessages.type = "Please select whether this webhook is for a repository or organization";
        }

        if (!scope.trim()) {
            newErrorMessages.scope =
                type === WebhookType.SCM_REPOSITORY
                    ? "Please enter the repository path (e.g., owner/repo)"
                    : type === WebhookType.SCM_ORGANIZATION
                      ? "Please enter the organization name"
                      : "Please enter the scope for this webhook";
        } else if (scope.length > 255) {
            newErrorMessages.scope = "Scope must be 255 characters or less";
        }

        if (!provider) {
            newErrorMessages.provider = "Please select a Git provider";
        }

        store.set(atoms.errorMessagesAtom, newErrorMessages);
        return Object.keys(newErrorMessages).length === 0;
    }, [store, atoms]);

    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();

            if (!validateForm()) {
                return;
            }

            const name = store.get(atoms.nameAtom);
            const description = store.get(atoms.descriptionAtom);
            const type = store.get(atoms.typeAtom);
            const scope = store.get(atoms.scopeAtom);
            const provider = store.get(atoms.providerAtom);

            try {
                const webhook = await createWebhook({
                    name: name.trim(),
                    description: description.trim() || undefined,
                    type: type!,
                    scope: scope.trim(),
                    provider: provider!,
                });
                toast({
                    title: "Webhook created",
                    description: `"${webhook.metadata?.name}" has been created successfully.`,
                });
                resetForm();
                onSuccess(webhook.id);
            } catch (error) {
                toast({
                    title: "Failed to create webhook",
                    description: formatError(error),
                });
            }
        },
        [store, atoms, validateForm, createWebhook, toast, resetForm, onSuccess],
    );

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent data-testid="create-webhook-dialog" data-track-location={TrackLocations.CreateWebhookDialog}>
                <DialogHeader>
                    <DialogTitle>Add Webhook</DialogTitle>
                    <DialogDescription>
                        Create a new webhook to receive events from your SCM provider.
                    </DialogDescription>
                </DialogHeader>
                <DialogBody className="flex flex-col gap-4">
                    <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
                        <NameField atoms={atoms} />
                        <DescriptionField atoms={atoms} />
                        <ProviderField atoms={atoms} />
                        <TypeField atoms={atoms} />
                        <ScopeField atoms={atoms} />
                    </form>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="create-webhook-cancel" type="button">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="primary"
                        type="submit"
                        form={formId}
                        loading={isPending}
                        disabled={isPending}
                        data-testid="create-webhook-submit"
                        data-tracking-id="create-webhook-submit"
                    >
                        Create Webhook
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

/**
 * Creates all atoms needed for the webhook creation form.
 * These atoms are scoped to this component instance.
 */
function useFormAtoms(): FormAtoms {
    return useMemo(() => {
        const nameAtom = atom("");
        const descriptionAtom = atom("");
        const typeAtom = atom<WebhookType | null>(null);
        const scopeAtom = atom("");
        const providerAtom = atom<WebhookProvider | null>(null);
        const errorMessagesAtom = atom<FormErrorMessages>({});

        return {
            nameAtom,
            descriptionAtom,
            typeAtom,
            scopeAtom,
            providerAtom,
            errorMessagesAtom,
        };
    }, []);
}

// Field components
const NameField: FC<{ atoms: FormAtoms }> = ({ atoms }) => {
    const [name, setName] = useAtom(atoms.nameAtom);
    const [errorMessages] = useAtom(atoms.errorMessagesAtom);

    return (
        <InputField label="Name" id="webhook-name" error={errorMessages.name}>
            <Input
                id="webhook-name"
                placeholder="My Webhook"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="webhook-name-input"
                maxLength={80}
            />
        </InputField>
    );
};

const DescriptionField: FC<{ atoms: FormAtoms }> = ({ atoms }) => {
    const [description, setDescription] = useAtom(atoms.descriptionAtom);

    return (
        <InputField label="Description" id="webhook-description" hint="Optional description for this webhook">
            <Input
                id="webhook-description"
                placeholder="Webhook for triggering automations"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="webhook-description-input"
                maxLength={500}
            />
        </InputField>
    );
};

const ProviderField: FC<{ atoms: FormAtoms }> = ({ atoms }) => {
    const [provider, setProvider] = useAtom(atoms.providerAtom);
    const [errorMessages] = useAtom(atoms.errorMessagesAtom);

    const providerOptions = [
        { value: WebhookProvider.GITHUB, config: WEBHOOK_PROVIDER_CONFIG[WebhookProvider.GITHUB] },
        { value: WebhookProvider.GITLAB, config: WEBHOOK_PROVIDER_CONFIG[WebhookProvider.GITLAB] },
    ];

    return (
        <InputField label="Provider" id="webhook-provider" error={errorMessages.provider}>
            <Select
                value={provider?.toString() ?? ""}
                onValueChange={(value) => setProvider(parseInt(value) as WebhookProvider)}
            >
                <SelectTrigger loading={false} data-testid="webhook-provider-select">
                    <SelectValue placeholder="Select a provider" />
                </SelectTrigger>
                <SelectContent>
                    {providerOptions.map(({ value, config }) => {
                        const Icon = config.icon;
                        return (
                            <SelectItem
                                key={value}
                                value={value.toString()}
                                data-testid={`webhook-provider-${config.name.toLowerCase()}`}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon size="sm" className="text-content-primary" />
                                    <span>{config.name}</span>
                                </div>
                            </SelectItem>
                        );
                    })}
                </SelectContent>
            </Select>
        </InputField>
    );
};

const TypeField: FC<{ atoms: FormAtoms }> = ({ atoms }) => {
    const [type, setType] = useAtom(atoms.typeAtom);
    const setScope = useSetAtom(atoms.scopeAtom);
    const [errorMessages] = useAtom(atoms.errorMessagesAtom);

    const handleTypeChange = (value: string) => {
        const newType = parseInt(value) as WebhookType;
        if (newType !== type) {
            setScope("");
        }
        setType(newType);
    };

    const typeOptions = [
        { value: WebhookType.SCM_REPOSITORY, config: WEBHOOK_TYPE_CONFIG[WebhookType.SCM_REPOSITORY] },
        { value: WebhookType.SCM_ORGANIZATION, config: WEBHOOK_TYPE_CONFIG[WebhookType.SCM_ORGANIZATION] },
    ];

    return (
        <InputField label="Type" id="webhook-type" error={errorMessages.type}>
            <Select value={type?.toString() ?? ""} onValueChange={handleTypeChange}>
                <SelectTrigger loading={false} data-testid="webhook-type-select">
                    <SelectValue placeholder="Select a type" />
                </SelectTrigger>
                <SelectContent>
                    {typeOptions.map(({ value, config }) => (
                        <SelectItem
                            key={value}
                            value={value.toString()}
                            data-testid={`webhook-type-${config.name.toLowerCase()}`}
                        >
                            <span>{config.name}</span>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </InputField>
    );
};

const ScopeField: FC<{ atoms: FormAtoms }> = ({ atoms }) => {
    const [scope, setScope] = useAtom(atoms.scopeAtom);
    const [type] = useAtom(atoms.typeAtom);
    const [errorMessages] = useAtom(atoms.errorMessagesAtom);

    const getScopeLabel = () => {
        if (type === WebhookType.SCM_REPOSITORY) {
            return "Repository name";
        }
        if (type === WebhookType.SCM_ORGANIZATION) {
            return "Organization name";
        }
        return "Scope";
    };

    const getScopeHint = () => {
        if (type === WebhookType.SCM_REPOSITORY) {
            return 'Enter the repository path (e.g., "owner/repo")';
        }
        if (type === WebhookType.SCM_ORGANIZATION) {
            return 'Enter the organization name (e.g., "owner")';
        }
        return "Select a type first to see the expected format";
    };

    const getScopePlaceholder = () => {
        if (type === WebhookType.SCM_REPOSITORY) {
            return "owner/repo";
        }
        if (type === WebhookType.SCM_ORGANIZATION) {
            return "owner";
        }
        return "";
    };

    return (
        <InputField label={getScopeLabel()} id="webhook-scope" error={errorMessages.scope} hint={getScopeHint()}>
            <Input
                id="webhook-scope"
                placeholder={getScopePlaceholder()}
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                data-testid="webhook-scope-input"
                maxLength={255}
            />
        </InputField>
    );
};
